import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Button } from '@fluentui/react-components';
import { Dismiss16Regular, DocumentBulletList24Regular, Pin20Regular } from '@fluentui/react-icons';
import { GuideDrawer, GuideDrawerSection } from '../../../theme/components/GuideDrawer';
import { GuideEmptyState } from '../../../theme/components/GuideEmptyState';
import { StatusPill } from '../../../theme/components/StatusPill';
import ContractSelector from '../../../commons/component/Inputs/ContractSelector/ContractSelectorComponent';
import { Contract } from '../../../models/Entities/Contracts/Contract';
import { useContracts } from '../Dashboard.Hooks';
import { PinnedContractsSettings, WidgetProps } from '../Dashboard.Types';
import { WidgetData } from './WidgetData';
import { contractStatusKey, contractStatusTone, pinnedContracts } from './Widgets.Functions';

const formatDate = (value: Date | string | undefined): string => (value ? moment(value).format('DD/MM/YYYY') : '—');

export const pinnedIdsOf = (settings: WidgetProps['widget']['settings']): number[] => {
  const ids = (settings as Partial<PinnedContractsSettings> | undefined)?.contractIds;
  return Array.isArray(ids) ? ids.filter((id): id is number => typeof id === 'number') : [];
};

/** Contratos que el usuario fijó como acceso directo; la selección se guarda en la configuración del widget. */
export const PinnedContractsWidget = ({ widget, editing, settingsOpen, onCloseSettings, onSettingsChange }: WidgetProps) => {
  const { t } = useTranslation();
  const contracts = useContracts();
  const ids = React.useMemo(() => pinnedIdsOf(widget.settings), [widget.settings]);
  const pinned = React.useMemo(() => pinnedContracts(contracts.list, ids), [contracts.list, ids]);
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<number[]>(ids);

  const drawerOpen = open || settingsOpen;
  React.useEffect(() => {
    if (drawerOpen) setDraft(ids);
  }, [drawerOpen, ids]);

  const close = () => {
    setOpen(false);
    onCloseSettings();
  };

  const save = () => {
    onSettingsChange({ contractIds: draft });
    close();
  };

  const unpin = (id: number) => onSettingsChange({ contractIds: ids.filter((candidate) => candidate !== id) });

  const onSelected = (selected: Contract[]) => setDraft(selected.map((contract) => contract.id));

  return (
    <WidgetData loading={contracts.loading} failed={contracts.failed} error={contracts.error} onRetry={contracts.reload}>
      {pinned.length === 0 ? (
        <GuideEmptyState
          icon={<DocumentBulletList24Regular />}
          title={t('dashboard.widgets.pinnedContracts.empty')}
          text={t('dashboard.widgets.pinnedContracts.emptyText')}
          action={
            <Button appearance="primary" icon={<Pin20Regular />} onClick={() => setOpen(true)} data-testid="pinned-contracts-pin">
              {t('dashboard.widgets.pinnedContracts.pin')}
            </Button>
          }
          data-testid="pinned-contracts-empty"
        />
      ) : (
        <>
          <ul className="dbw-list" data-testid="pinned-contracts-list">
            {pinned.map((contract) => (
              <li key={contract.id} className="dbw-list__row">
                <div className="dbw-list__main">
                  <Link to={`/contracts/${contract.id}`} className="dbw-list__title" data-testid={`pinned-contract-${contract.id}`}>
                    {contract.contractNumber}
                    {contract.contractDescription ? ` · ${contract.contractDescription}` : ''}
                  </Link>
                  <span className="dbw-list__meta">
                    {t('dashboard.widgets.pinnedContracts.validity')} {formatDate(contract.contractStartDate)} – {formatDate(contract.contractEndDate)}
                    {contract.contractCurrency ? ` · ${contract.contractCurrency}` : ''}
                  </span>
                </div>
                <div className="dbw-list__side">
                  <StatusPill tone={contractStatusTone(contract.contractStatus)} size="small">
                    {t(`dashboard.widgets.pinnedContracts.status.${contractStatusKey(contract.contractStatus)}`)}
                  </StatusPill>
                  {editing && (
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<Dismiss16Regular />}
                      aria-label={t('dashboard.widgets.pinnedContracts.unpin', { name: contract.contractNumber })}
                      title={t('dashboard.widgets.pinnedContracts.unpin', { name: contract.contractNumber })}
                      onClick={() => unpin(contract.id)}
                      data-testid={`pinned-contract-unpin-${contract.id}`}
                    />
                  )}
                </div>
              </li>
            ))}
          </ul>
          <div className="dbw-actions">
            <Button appearance="subtle" size="small" icon={<Pin20Regular />} onClick={() => setOpen(true)} data-testid="pinned-contracts-edit">
              {t('dashboard.widgets.pinnedContracts.pin')}
            </Button>
          </div>
        </>
      )}

      <GuideDrawer
        open={drawerOpen}
        onDismiss={close}
        size="medium"
        title={t('dashboard.widgets.pinnedContracts.settingsTitle')}
        subtitle={t('dashboard.widgets.pinnedContracts.settingsText')}
        primaryAction={{ label: t('dashboard.common.save'), onClick: save, testId: 'pinned-contracts-save' }}
        testId="pinned-contracts-drawer"
      >
        <GuideDrawerSection title={t('dashboard.widgets.pinnedContracts.title')}>
          {drawerOpen && <ContractSelector onSelectedContract={onSelected} ContractsSelected={draft} MultiselectEnable={true} />}
        </GuideDrawerSection>
      </GuideDrawer>
    </WidgetData>
  );
};

export default PinnedContractsWidget;
